import React, {FormEvent, useContext, useEffect, useRef} from "react";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import {EngageAppContext} from "../../App";
import {TriviaAction} from "../../messages/messages";
import SendIcon from "../common/SendIcon";
import {ChatContext, ChatUIState} from "../../contexts/ChatContext";
import NotificationBubble from "../common/NotificationBubble";
import clsx from "clsx";


const useStyles = createUseStyles<Theme>((theme) => ({
    chatInput: {
        position:"relative",
        backgroundColor: "white",
        border: theme.chat.inputBorder,
        borderRadius: 5,
    },
    inputForm: {
        height: 50,
        display:"flex",
        alignItems: "center",
        justifyContent: "center",
        padding: [10,10,10,16]
    },
    textfield: {
        flex: 1,
        border: "none",
        background: "none",
        outline: "none",
        color: "black",
        resize: "none",
        overflow: "hidden",
        fontFamily: '"Anova", "Roboto", "Helvetica", "Arial", "sans-serif"',
        padding: [0,10],
        lineHeight: 1.4,
        fontSize: 14
    },
    sendButton: {
        width: 34,
        height: 34,
        borderRadius: 17,
        border: "none",
        background: theme.chat.sendButton.background,
        userSelect: "none",
        cursor: "pointer",
        outline:0,
        textDecoration: "none",
        "-webkit-appearance": "none",
        "-webkit-tap-highlight-color": "transparent",
        "&:disabled": {
            cursor: "default",
            opacity: .7
        }
    },
    iconWrapper: {
        display: "flex",
        justifyContent: "center",
        paddingLeft: 2,
        color:"white",
    },
    noteBubble: {
        position: "absolute",
        top: 2,
        left: 2,
        opacity: 1,
        "&.hidden" : {
            opacity: 0
        }
    }
}));

type ChatInputProps = {
    setChatUIState: (newState:ChatUIState)=>void
    shouldFocus: boolean
}

const ChatInput = ({setChatUIState, shouldFocus}:ChatInputProps) => {
    const {sendMessage, appState} = useContext(EngageAppContext.current);
    const {chatReadCount, chatUIState, messageInput, setMessageInput, selectionRange, setSelectionRange} = useContext(ChatContext);
    const classes = useStyles();
    const theme:Theme = useTheme();
    const textArea = useRef<HTMLTextAreaElement>(null);

    const unreadCount = chatUIState !== ChatUIState.focused ? appState.chat.entries.length - chatReadCount : 0;
    const unreadLabel = unreadCount > 99 ? "99+" : unreadCount;

    useEffect(()=>{
        if(chatUIState === ChatUIState.focused || shouldFocus) {
            textArea.current?.setSelectionRange(selectionRange.start, selectionRange.end);
            textArea.current?.focus();
        }
        //eslint-disable-next-line
    },[]);

    function submitMessage(event?:FormEvent) {
        event?.preventDefault();
        if(messageInput?.length) {
            sendMessage({
                type: TriviaAction.Chat,
                text: messageInput
            });
            setMessageInput("");
            setSelectionRange({start:0,end:0});
        }
        textArea.current?.focus();
    }

    function selectionChanged() {
        if(textArea.current) {
            setSelectionRange({start: textArea.current.selectionStart, end: textArea.current.selectionEnd});
        }
    }

    function keyPress(event:React.KeyboardEvent) {
        if(event.key === "Enter" && !event.shiftKey){
            submitMessage();
            event.preventDefault();
        }
    }

    return (
            <div className={classes.chatInput}>
                <NotificationBubble className={clsx(classes.noteBubble, unreadCount === 0 && "hidden")}>
                    {unreadLabel}
                </NotificationBubble>
                <form className={classes.inputForm} onSubmit={submitMessage}>
                    <textarea name="chat-input" value={messageInput}
                              onChange={(e)=>setMessageInput(e.currentTarget.value)}
                              className={classes.textfield} autoComplete="no"
                              onBlur={()=>setChatUIState(ChatUIState.none)}
                              onFocus={()=>setChatUIState(ChatUIState.focused)}
                              disabled={false} placeholder="Type your chat message"
                              onKeyPress={keyPress} ref={textArea} onSelect={selectionChanged}
                    />
                    <button className={classes.sendButton} disabled={messageInput.length === 0}>
                        <div className={classes.iconWrapper}>{theme.chat.sendButton.icon ? <img src={theme.chat.sendButton.icon} alt="Send Icon"/> : <SendIcon />}</div>
                    </button>
                </form>
            </div>
    );
};

export default ChatInput;